"use client";
import { useEffect } from "react";
import { ExternalLink, FileText, Shield, X } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function TransparencyModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation("common");

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="transparency-modal-title"
        className="w-full max-w-lg rounded-2xl bg-white p-8 shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Shield className="h-6 w-6 text-gray-900" />
            <h2
              id="transparency-modal-title"
              className="font-manrope text-xl font-semibold text-gray-900"
            >
              {t("footer.transparencyModal.title")}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-full border border-gray-200 p-2 text-gray-500 hover:bg-gray-50 hover:text-gray-900"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="mt-6 text-sm/6 text-gray-600">
          {t("footer.transparencyModal.description")}
        </p>

        <div className="mt-6 border-t border-gray-100 pt-6">
          <div className="flex items-center justify-between gap-4 rounded-xl border border-gray-100 bg-gray-50 p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#f9b131]/15">
                <FileText className="h-5 w-5 text-[#f9b131]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  {t("footer.transparencyModal.documentName")}
                </p>
                <p className="text-xs text-gray-500">
                  {t("footer.transparencyModal.documentMeta")}
                </p>
              </div>
            </div>
            <a
              href="/doc/Bilancio_sociale_2025.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-full border border-gray-300 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-100"
            >
              <ExternalLink className="h-4 w-4" />
              {t("footer.transparencyModal.download")}
            </a>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-gray-400">
          {t("footer.transparencyModal.footnote")}
        </p>
      </div>
    </div>
  );
}
